import React, { useCallback, useMemo } from 'react'
import { rem } from 'polished'
import styled from 'styled-components'
import BodyText from 'LuxKit/Typography/BodyText'
import LineArrowRightIcon from 'LuxKit/Icons/line/LineArrowRightIcon'
import Caption from 'LuxKit/Typography/Caption'
import LineHistoryIcon from 'LuxKit/Icons/line/LineHistoryIcon'
import { tripTypeToFareTypeMapper } from 'api/flights'
import { bumpRecentFlightSearch, deriveFareType, type StandaloneFlightSearchParam } from 'lib/flights/flightUtils'
import { fareCabinLabelMap, fareTypeLabelMap, FlightsClassTypes, FlightsFareTypes, fareClassToUrlMap } from 'constants/flight'
import qs from 'qs'
import moment from 'moment'
import { ISO_DATE_FORMAT } from 'constants/dateFormats'
import Clickable from 'components/Common/Clickable'
import { mediaHoverable } from 'lib/theme/mediaQueries'
import Group from 'components/utils/Group'
import pluralize from 'lib/string/pluralize'
import LineArrowsSwapHorizontalIcon from 'LuxKit/Icons/line/LineArrowsSwapHorizontalIcon'
import { mediaQueryUp } from 'components/utils/breakpoint'

type FlightQueryParams = StandaloneFlightSearchParam & { searchId: string }

const RecentSearchContainer = styled(Clickable)`
  display: flex;
  padding: ${rem(8)};
  flex-direction: column;
  gap: ${rem(8)};
  justify-content: flex-start;
  align-items: flex-start;
  transition: background-color 0.2s;
  min-width: 100%;

  ${mediaQueryUp.tablet} {
    min-width: ${rem(350)};
  }

  ${mediaHoverable} {
    &:hover {
      background-color: ${props => props.theme.palette.neutral.default.seven};
    }
  }
`

const IconBlock = styled.div`
  width: ${rem(48)};
  height: ${rem(48)};
  padding: ${rem(12)};
  background-color: ${props => props.theme.palette.neutral.default.seven};
  display: flex;
  align-items: center;
  justify-content: center;
`

const SubHeader = styled(Caption)`
  color: ${props => props.theme.palette.neutral.default.two};
  font-weight: ${props => props.theme.font.primary.weight.regular};
`

const InlineBodyText = styled(BodyText)`
  display: inline-block;
  text-align: center;
  max-width: ${rem(100)};
`

function getStandaloneFlightSearchUrlFromRecentSearch(recentSearch: App.RecentFlightSearch) {
  const mappedFareType = tripTypeToFareTypeMapper[recentSearch.tripType]
  const hasSecondLeg = recentSearch.itinerary.length > 1
  const fareType = deriveFareType(hasSecondLeg, mappedFareType)

  const firstLeg = recentSearch.itinerary[0]
  const secondLeg = recentSearch.itinerary[1]

  const departDate = moment(firstLeg.departureDate, 'DD/MM/YYYY').format(ISO_DATE_FORMAT)
  const returnDate = fareType === FlightsFareTypes.RETURN && secondLeg?.departureDate
    ? moment(secondLeg.departureDate, 'DD/MM/YYYY').format(ISO_DATE_FORMAT)
    : null
  const internalFareClass = recentSearch.fareClass ?? FlightsClassTypes.ECONOMY
  const queryParams: FlightQueryParams = {
    originAirportCode: firstLeg.departureAirport,
    originAirportName: firstLeg.departureAirportName,
    destinationAirportCode: firstLeg.arrivalAirport,
    destinationAirportName: firstLeg.arrivalAirportName,
    departDate,
    returnDate,
    adults: recentSearch.passengers.numAdults,
    children: recentSearch.passengers.numChildren,
    infants: recentSearch.passengers.numInfants,
    fareClass: fareClassToUrlMap[internalFareClass as FlightsClassTypes] ?? fareClassToUrlMap[FlightsClassTypes.ECONOMY],
    fareType,
    searchId: recentSearch.searchId,
  }

  if (fareType === FlightsFareTypes.MULTI_CITY) {
    const flights = recentSearch.itinerary.map(itinerary => ({
      originAirportCode: itinerary.departureAirport,
      originAirportName: itinerary.departureAirportName,
      destinationAirportCode: itinerary.arrivalAirport,
      destinationAirportName: itinerary.arrivalAirportName,
      departDate: moment(itinerary.departureDate, 'DD/MM/YYYY').format(ISO_DATE_FORMAT),
    }))
    return `/flights-search-results-multi-city?${qs.stringify({ ...queryParams, flights }, { skipNulls: true })}`
  }

  return `/flights-search-results?${qs.stringify(queryParams, { skipNulls: true })}`
}

function getSubHeader(recentSearch: App.RecentFlightSearch, fareTypeLabel: string | undefined) {
  const fareClassLabel = recentSearch.fareClass && fareCabinLabelMap[recentSearch.fareClass as FlightsClassTypes]

  const datesLabel = recentSearch.itinerary.map((itinerary, index) => {
    const dateFormat = index === recentSearch.itinerary.length - 1 ? 'D MMM YYYY' : 'D MMM'
    return moment(itinerary.departureDate, 'DD/MM/YYYY').format(dateFormat)
  }).join(' - ')

  const totalTravellers = recentSearch.passengers.numAdults + recentSearch.passengers.numChildren + recentSearch.passengers.numInfants
  const { text: travellerLabel } = pluralize({ singular: 'traveller', count: totalTravellers })

  const elements = [
    fareClassLabel,
    datesLabel,
    `${totalTravellers} ${travellerLabel}`,
    fareTypeLabel,
  ]

  return elements.filter(Boolean).join(' · ')
}

export interface RecentFlightSearchProps {
  recentSearch: App.RecentFlightSearch
}

function getMultiCityItineraryLabel(itinerary: Array<App.RecentFlightSearchItineraryLeg>) {
  const cities = Array.from(new Set(itinerary.map(({ departureCityDesc, arrivalCityDesc }) => [departureCityDesc, arrivalCityDesc]).flat()))

  return cities.join(', ')
}

function RecentFlightSearch({ recentSearch }: RecentFlightSearchProps) {
  const searchUrl = getStandaloneFlightSearchUrlFromRecentSearch(recentSearch)
  const mappedFareType = useMemo(() => tripTypeToFareTypeMapper[recentSearch.tripType], [recentSearch.tripType])
  const hasSecondLegWithDate = useMemo(() => !!recentSearch.itinerary[1]?.departureDate, [recentSearch.itinerary])
  const fareType = useMemo(() => deriveFareType(hasSecondLegWithDate, mappedFareType), [hasSecondLegWithDate, mappedFareType])
  const onClickRecent = useCallback(() => bumpRecentFlightSearch(recentSearch), [recentSearch])
  const fareTypeLabel = fareTypeLabelMap[fareType]

  return (
    <RecentSearchContainer to={searchUrl} onClick={onClickRecent}>
      <Group direction="horizontal" gap={12}>
        <IconBlock>
          <LineHistoryIcon size="M" />
        </IconBlock>
        <Group direction="vertical" gap={4}>
          {fareType === FlightsFareTypes.MULTI_CITY && <BodyText variant="medium" colour="neutral-one" wrap="pretty" weight="bold">{getMultiCityItineraryLabel(recentSearch.itinerary)}</BodyText>}
          {fareType !== FlightsFareTypes.MULTI_CITY && <Group direction="horizontal" gap={4} horizontalAlign="space-between">
            <InlineBodyText variant="medium" colour="neutral-one" weight="bold" wrap="truncate" align="center">{`${recentSearch.itinerary[0].departureCityDesc || recentSearch.itinerary[0].departureAirportName}`}</InlineBodyText>
            <InlineBodyText variant="medium" colour="neutral-one" weight="bold" wrap="truncate" align="center">{`(${recentSearch.itinerary[0].departureAirport})`}</InlineBodyText>
            {fareType === FlightsFareTypes.ONE_WAY && <LineArrowRightIcon size="S" />}
            {fareType === FlightsFareTypes.RETURN && <LineArrowsSwapHorizontalIcon size="S" />}
            <InlineBodyText variant="medium" colour="neutral-one" weight="bold" wrap="truncate" align="center">{`${recentSearch.itinerary[0].arrivalCityDesc || recentSearch.itinerary[0].arrivalAirportName}`}</InlineBodyText>
            <InlineBodyText variant="medium" colour="neutral-one" weight="bold" wrap="truncate" align="center">{`(${recentSearch.itinerary[0].arrivalAirport})`}</InlineBodyText>
          </Group>}
          <SubHeader wrap="pretty" variant="medium">{getSubHeader(recentSearch, fareTypeLabel)}</SubHeader>
        </Group>
      </Group>
    </RecentSearchContainer>
  )
}

export default RecentFlightSearch
